import * as React from 'react';
import { LanguageContext } from '../utilities/LocalizationModule';
import TopNavigator from "../components/TopNavigator"
import { DefaultButton, Stack } from "@fluentui/react"
import { HomeView } from "../data-structures/enums"
import UsersTable from '../components/displays/UsersTable';
import { connect, ConnectedProps } from 'react-redux';
import { IPortalUser, ITenant, IUserProfileRequest } from '../data-structures/interfaces';
import { getCurrentProfile, setAuthToken } from '../utilities/helpers/ApiHelper';
import { setUserProfile } from "../redux/modules/user"
import { getRequests } from "../redux/modules/requests"
import { getDatasourceTypes } from "../redux/modules/datasource"
import Dashboard from '../components/displays/Dashboard';
import Subscriptions from '../components/displays/Subsriptions';
import MessageScreen from "./MessageScreen"
import NavMenu from "../components/NavMenu"
import { MainViewStyle } from "../styles/HomeStyles"
import { useMsal } from '@azure/msal-react';
import Loading from '../components/Loading';
import { RootState } from '../redux';
import PartnersOverview from "../components/displays/PartnersOverview";
import Requests from "../components/displays/Requests";
import { AuthHelper } from '../utilities/helpers/AuthHelper';

const mapDispatchToProps = {
    setUserProfile,
    getRequests,
    getDatasourceTypes
};

const mapStateToProps = (state: RootState) => {
    return {
        requests: state.requests.requests
    }
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
interface HomeProps extends PropsFromRedux { }

const Home: React.FC<HomeProps> = ({
    setUserProfile,
    requests,
    getRequests,
    getDatasourceTypes
}: HomeProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [currentMainView, setCurrentMainView] = React.useState<HomeView>(HomeView.Dashboard);
    const [isUnauthorized, setIsUnauthorized] = React.useState<boolean>(false);
    const [userTenantName, setUserTenantName] = React.useState<string>("");

    const { instance, accounts } = useMsal();

    const [mollieTransactionId, setMollieTransactionId] = React.useState("");
    const [mollieTenant, setMollieTenant] = React.useState<ITenant>();

    React.useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const trns = searchParams.get('trns');
        if (trns) {
            setMollieTransactionId(trns);
            setCurrentMainView(HomeView.Subscriptions)
        }
    }, []);

    React.useEffect(() => {
        updateData();
    }, []);

    const updateData = async () => {
        setIsLoading(true);
        try {
            AuthHelper.setSignOutCallback(signOut);
            const profileRequest: IUserProfileRequest = { accountId: accounts[0]?.homeAccountId, username: accounts[0]?.username, clientId: accounts[0]?.idTokenClaims?.aud };
            if (await setAuthToken(profileRequest)) {
                let userProfile: IPortalUser = await getCurrentProfile(profileRequest);
                if (userProfile) {
                    let userTenantName: string;
                    userProfile.tenants = userProfile.tenants.map(tenant => {

                        if (!userTenantName && tenant.tenantId === userProfile.tenantId && tenant.friendlyName) {
                            userTenantName = tenant.friendlyName.charAt(0).toUpperCase() + tenant.friendlyName.slice(1);
                            setUserTenantName(userTenantName);
                        }

                        const advancedFields = (tenant.allowedAdvancedSearchFields as string)?.trim().toLowerCase().split(";") ?? [];
                        const allowedTeams = (tenant.allowedTeams as string)?.trim().toLowerCase().split(";") ?? [];
                        const allowedSiteCollections = (tenant.allowedSiteCollections as string)?.trim().toLowerCase().split(";") ?? [];
                        const hiddenMetadataFields = (tenant.hiddenMetadataFields as string)?.trim().toLowerCase().split(";") ?? [];
                        return ({
                            ...tenant,
                            allowedAdvancedSearchFields: advancedFields,
                            allowedSiteCollections,
                            allowedTeams,
                            hiddenMetadataFields,
                        });
                    });
                    setUserProfile(userProfile, accounts[0]);
                    if (requests.length === 0 && userProfile.isGlobalAdmin) getRequests();
                    getDatasourceTypes();
                }
                else {
                    setIsUnauthorized(true);
                }
            }
            else {
                signOut();
            }
        } catch (e) {
            console.error(e);
        }

        setIsLoading(false);
    };

    const signOut = () => {
        instance.logoutRedirect();
    }

    return isLoading
        ? <Loading label={languageStrings.LoadingPortal} />
        : !isUnauthorized
            ? <React.Fragment>
                <TopNavigator userTenantName={userTenantName} signOut={signOut} />
                <Stack horizontal>
                    <Stack.Item>
                        <NavMenu setSelectedView={setCurrentMainView} />
                    </Stack.Item>
                    {currentMainView === HomeView.Dashboard ?
                        <Stack.Item className={MainViewStyle}>
                            <Dashboard
                                updateData={updateData}
                                browseToSubscription={(tenant: ITenant) => {
                                    setMollieTenant(tenant);
                                    setCurrentMainView(HomeView.Subscriptions);
                                }}
                            />
                        </Stack.Item> : null}
                    {currentMainView === HomeView.Users
                        ? <Stack.Item className={MainViewStyle}>
                            <UsersTable />
                        </Stack.Item> : null}
                    {currentMainView === HomeView.PartnersOverview ?
                        <Stack.Item className={MainViewStyle}>
                            <PartnersOverview />
                        </Stack.Item> : null}
                    {currentMainView === HomeView.PartnersRequests ?
                        <Stack.Item className={MainViewStyle}>
                            <Requests />
                        </Stack.Item> : null}
                    {currentMainView === HomeView.Subscriptions ?
                        <Stack.Item className={MainViewStyle}>
                            <Subscriptions transactionId={mollieTransactionId} defaultTenant={mollieTenant} />
                        </Stack.Item> : null}
                </Stack>
            </React.Fragment>
            : <MessageScreen
                text={languageStrings.UnauthorizedMessage}
                actionButton={
                    <DefaultButton onClick={signOut}>
                        {languageStrings.SignInDifferentAccount}
                    </DefaultButton>
                }
            />
}

export default connector(Home);